"use client";

import { useId, useState } from "react";

import { useLanguage } from "../i18n/LanguageProvider";

/* ============================================================
   SUASANA NYAMAN SEPERTI DI RUMAH - VISI, MISI, MOTTO

   >>>>>> ⚠️ VISI-nya SUDAH KEDALUWARSA. BACA INI. <<<<<<

   Visi resmi yang tertulis di situs Anda berbunyi "...pada tahun
   2024". Sekarang Agustus 2026 - target itu lewat dua tahun lalu.

   Saya salin apa adanya karena mengubah visi rumah sakit BUKAN
   keputusan yang boleh diambil dari sini. Visi dan misi adalah
   dokumen yang dipakai saat penilaian KARS, dan versi di situs harus
   sama persis dengan versi yang diserahkan ke sana.

   Tapi tolong tanyakan ke manajemen: apakah visinya sudah
   diperbarui? Kalau sudah, ganti teks di bawah. Kalau belum, halaman
   About yang menayangkan target tahun 2024 di tahun 2026 terbaca
   sebagai situs yang tidak diurus - dan itu justru kesan yang paling
   ingin dihindari di halaman profil.

   Kalau tahunnya dihapus tanpa persetujuan, isi situs jadi berbeda
   dengan dokumen resmi. Jadi jangan diam-diam dibetulkan sendiri.

   ============================================================
   PERUBAHAN DARI REFERENSI YANG ANDA KIRIM

   1. KUNING TURUN JADI AKSEN, bukan latar. Di referensi ketiga
      bilahnya kuning penuh - selebar kolom, bobot visualnya
      menyaingi judul halaman, padahal ini konten sekunder. Sekarang
      kuning hanya muncul di kotak ikon dan batang penanda di tepi
      kiri baris yang terbuka.

   2. NOMOR PADA MISI PUNYA INDENT GANTUNG. Di referensi, baris kedua
      tiap butir masuk ke bawah nomornya sehingga nomornya seperti
      melayang. Sekarang teks yang membungkus rata dengan huruf
      pertama, bukan dengan nomor.

   3. ISI PANEL DIBERI JARAK. Di referensi teksnya nyaris menempel ke
      tepi bilah.

   4. IKON + BERPUTAR JADI x saat terbuka, bukan berganti gambar.
      Perputarannya memberi tahu mata bahwa yang terbuka adalah yang
      barusan ditekan - berguna kalau nanti isinya bertambah.

   5. FOTO DIHILANGKAN, sesuai permintaan.

   6. DITANAM DI KOLOM KANAN HERO, bukan jadi section sendiri. Di
      versi sebelumnya ia berdiri sendiri di bidang putih dengan ruang
      kosong lebar di kanannya, sementara kolom kanan hero justru
      menganga kosong di bawah tombol. Sekarang keduanya beres
      sekaligus: ruang kosong terisi, dan tinggi kolom kanan jadi
      seimbang dengan foto gedung di kiri.

   ============================================================
   CATATAN TEKNIS

   - "use client" wajib: komponen ini punya useState dan onClick.
     Karena itu ia berkas terpisah - halaman About mengekspor
     metadata, dan itu hanya boleh di server component.
   - Panel dibuka dengan grid-template-rows 0fr -> 1fr, cara yang
     sama dipakai ServiceAccordion.jsx di project ini. Ini
     menganimasikan tinggi otomatis tanpa mengukur lewat JavaScript;
     max-height mengharuskan menebak angka - kekecilan memotong isi,
     kebesaran membuat animasinya terasa lambat di awal.
   - className ditulis satu baris - alasan CRLF yang sama seperti di
     Facility.jsx dan Footer.jsx.
   ============================================================ */

/* Judul pindah ke kamus: visimisi.judul. Dipakai sebagai label kecil
   di atas akordeon, bukan judul besar - judul besar "Rumah Sakit
   Jakarta Medical Center" sudah ada di kolom yang sama. */

/* Item pertama terbuka sejak awal. Akordeon yang seluruhnya tertutup
   saat halaman dimuat terlihat seperti daftar mati - pengunjung tidak
   langsung tahu bahwa barisnya bisa dibuka. Setel ke -1 kalau ingin
   ketiganya tertutup. */
const TERBUKA_AWAL = 0;

/* Disalin apa adanya dari situs RS JMC. Jangan diubah tanpa
   persetujuan manajemen - lihat peringatan di kepala berkas. */
/* Teks pindah ke dictionary.js (kunci visimisi.*). Yang tertinggal di
   sini hanya KUNCI dan bentuk penyajiannya.

   ⚠️ VERSI INDONESIA ADALAH DOKUMEN YANG BERLAKU; versi Inggris di
   kamus adalah terjemahan kerja yang MASIH BUTUH PERSETUJUAN
   manajemen, sama seperti versi Indonesianya. Baca catatan lengkapnya
   di atas kunci `visimisi` di dictionary.js - termasuk soal "tahun
   2024" yang sudah lewat dan sengaja tidak dibetulkan. */
const ITEMS = [
  { key: "visi", judulKey: "visimisi.visi", tipe: "kutipan", isiKey: "visimisi.visiIsi" },
  {
    key: "misi",
    judulKey: "visimisi.misi",
    tipe: "daftar",
    isiKeys: [
      "visimisi.misi1",
      "visimisi.misi2",
      "visimisi.misi3",
      "visimisi.misi4",
      "visimisi.misi5",
    ],
  },
  { key: "motto", judulKey: "visimisi.motto", tipe: "kutipan", isiKey: "visimisi.mottoIsi" },
];


export default function VisiMisi() {
  const { t } = useLanguage();
  const [terbuka, setTerbuka] = useState(TERBUKA_AWAL);
  const baseId = useId();

  /* Menekan item yang sedang terbuka akan menutupnya. Satu terbuka
     dalam satu waktu - kalau ketiganya boleh terbuka bersamaan,
     kolomnya memanjang jauh melebihi foto di sebelahnya dan tata
     letak hero-nya pecah. */
  const toggle = (i) => setTerbuka((kini) => (kini === i ? -1 : i));

  return (
    /* TANPA <section>, TANPA padding halaman, TANPA max-w.

       Komponen ini sekarang ditanam DI DALAM kolom kanan hero halaman
       About, mengisi ruang kosong di bawah tombol. Jadi jarak dan
       lebarnya diatur oleh induknya, bukan oleh berkas ini - kalau
       diberi padding sendiri, ia tidak akan sejajar dengan paragraf
       di atasnya. */
    <div className="mt-8 border-t border-[var(--c-line)] pt-7 sm:mt-10 sm:pt-8">

      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--c-brand-mid)] sm:mb-4 sm:text-xs sm:tracking-[0.18em]">
        {t("visimisi.judul")}
      </p>

      {/* KARTU PUTIH, BUKAN TIGA BILAH KUNING.

         Referensi memakai kuning penuh pada ketiga bilah. Di layar
         itu jadi bidang kuning selebar kolom yang bobot visualnya
         menyaingi judul halaman - padahal ini konten sekunder.

         Sekarang kuningnya dipakai sebagai AKSEN saja: kotak ikon dan
         batang penanda di tepi kiri baris yang terbuka. Bidang
         utamanya putih dengan garis tepi tipis, sama seperti kartu
         Lokasi di bawah halaman ini - jadi keduanya terbaca sebagai
         satu keluarga. */}
      <div className="overflow-hidden rounded-xl bg-[var(--c-surface)] ring-1 ring-[var(--c-line)]">
        {ITEMS.map((item, i) => {
          const isOpen = i === terbuka;
          const panelId = `${baseId}-panel-${i}`;
          const buttonId = `${baseId}-button-${i}`;

          return (
            <div key={item.key} className={i > 0 ? "border-t border-[var(--c-line)]" : ""}>
              {/* aria-expanded dan aria-controls menghubungkan tombol
                 dengan panelnya. Tanpa keduanya, pembaca layar hanya
                 mendengar tombol tanpa tahu ia membuka apa, atau
                 apakah isinya sedang terbuka. */}
              <button
                type="button"
                id={buttonId}
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => toggle(i)}
                className={`relative flex w-full items-center gap-3 py-3.5 pl-4 pr-4 text-left transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--c-brand-mid)] sm:gap-3.5 sm:py-4 sm:pl-5 ${isOpen ? "bg-[var(--c-tint)]" : "hover:bg-slate-50"}`}
              >
                {/* Batang penanda di tepi kiri. scale-y dari tengah,
                   bukan muncul-hilang - jadi perpindahannya terbaca
                   sebagai satu penanda yang bergeser antar baris. */}
                <span aria-hidden="true" className={`absolute inset-y-0 left-0 w-[3px] origin-center bg-[var(--c-red)] transition-transform duration-300 ease-out motion-reduce:transition-none ${isOpen ? "scale-y-100" : "scale-y-0"}`} />

                {/* Kotak ikon. Satu-satunya tempat kuning muncul saat
                   tertutup, jadi ia yang menandai bahwa baris ini bisa
                   ditekan tanpa perlu mewarnai seluruh bilah. */}
                <span aria-hidden="true" className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors duration-200 ${isOpen ? "bg-[var(--c-brand)] text-white" : "bg-[var(--c-red)] text-white"}`}>
                  {/* Ikon + berputar 45 derajat jadi x. Satu bentuk,
                     bukan dua gambar yang bertukar - jadi tidak ada
                     kedipan saat berganti. */}
                  <svg viewBox="0 0 24 24" fill="none" className={`h-3.5 w-3.5 transition-transform duration-300 motion-reduce:transition-none ${isOpen ? "rotate-45" : ""}`}>
                    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                </span>

                <span className={`text-sm font-bold transition-colors duration-200 sm:text-base ${isOpen ? "text-[var(--c-brand)]" : "text-[var(--c-ink)]"}`}>
                  {t(item.judulKey)}
                </span>
              </button>

              <div id={panelId} role="region" aria-labelledby={buttonId} className={`grid transition-all duration-300 ease-out motion-reduce:transition-none ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                <div className="overflow-hidden">
                  {/* pl disamakan dengan lebar kotak ikon + gap, jadi
                     isi panel rata dengan judulnya, bukan dengan tepi
                     kartu. Ini yang membuatnya terbaca sebagai isi
                     DARI baris itu. */}
                  <div className="py-4 pl-[3.75rem] pr-4 sm:pl-[4.25rem] sm:pr-5">
                    {item.tipe === "daftar" ? (
                      /* list-decimal + pl memberi indent gantung:
                         baris yang membungkus rata dengan huruf
                         pertama, bukan masuk ke bawah nomornya. */
                      <ol className="list-decimal space-y-2 pl-4 text-[13px] leading-relaxed text-[var(--c-body)] marker:font-bold marker:text-[var(--c-brand-mid)] sm:text-sm">
                        {item.isiKeys.map((butirKey) => (
                          <li key={butirKey} className="pl-1">{t(butirKey)}</li>
                        ))}
                      </ol>
                    ) : (
                      <p className="text-[13px] italic leading-relaxed text-[var(--c-body)] sm:text-sm">
                        &ldquo;{t(item.isiKey)}&rdquo;
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
